import { Pagination, Table, TableColumnsType, Typography } from "antd";
import { useEffect, useState } from "react";

import { WorkflowComment, toArray, workflowApi } from "../../../../Services";
import { ClampedText } from "../../Onboarding/Steps/SOR";
import { commentFields } from "../../Constants";
import { getFormattedDate, hasKeyString, formatDocVersion } from "../../../../Utils/CommonUtils";

interface Props {
  envelopeId?: string;
  /** Bumped by the parent after a comment is posted, to force a reload. */
  refreshKey?: number;
}

const PAGE_SIZE = 10;

/**
 * Detail — Comments tab.
 *
 * Reads the trail from `GET /workflow/getCommentsList`, newest first
 * (`orderBy=commentedOn&order=desc`). The endpoint is paged, so the sort
 * and page live here and are pushed back to the server rather than being
 * applied to an already-fetched slice.
 */
export const Comments = ({ envelopeId, refreshKey = 0 }: Props) => {
  const [rows, setRows] = useState<WorkflowComment[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("commentedOn");

  useEffect(() => {
    if (!envelopeId) return;
    setIsLoading(true);
    workflowApi
      .commentList({ envelopeId, page, size: PAGE_SIZE, order, orderBy })
      .then((result: any) => {
        setRows(toArray<WorkflowComment>(result));
        setTotalRecords(result?.totalCount ?? 0);
      })
      .catch(() => setRows([])) // api.service has already surfaced the error
      .finally(() => setIsLoading(false));
  }, [envelopeId, page, order, orderBy, refreshKey]);

  /* The trail comes back in either spelling; commentFields reads both. */
  const field = (record: WorkflowComment, key: keyof ReturnType<typeof commentFields>) =>
    commentFields(record)[key] ?? "-";
  const columns: TableColumnsType<WorkflowComment> = [
    {
      title: "Name",
      dataIndex: "commenterName",
      sorter: true,
      width: 140,
      render: (_, record) => field(record, "name"),
    },
    {
      title: "Email Address",
      dataIndex: "commenterEmail",
      sorter: true,
      width: 180,
      render: (_, record) => field(record, "email"),
    },
    {
      title: "Document Name",
      dataIndex: "documentName",
      sorter: true,
      width: 220,
      render: (_, record) => commentFields(record).documentName ?? "", // blank when the comment is not on a document
    },
    {
      title: "Document Version",
      dataIndex: "documentVersion",
      sorter: true,
      width: 120,
      render: (_, record) => {
        const v = commentFields(record).version;
        return v !== undefined && v !== null && v !== "" && v !== "-" ? formatDocVersion(v) : "";
      },
    },
    {
      title: "Comments",
      dataIndex: "comment",
      sorter: true,
      /* Two lines, then "…"; the full comment shows on hover. */
      render: (_, record) => {
        /* Empty when there is no real comment — see commentFields. */
        const text = commentFields(record).comment;
        return text ? <ClampedText text={String(text)} rows={2} maxWidth={420} /> : "";
      },
    },
    {
      title: "Commented At",
      dataIndex: "commentedOn",
      sorter: true,
      width: 200,
      render: (value) => getFormattedDate(value) || "-",
    },
  ];

  return (
    <>
      <Typography.Title
        level={5}
        style={{ borderBottom: "2px solid #29256E", display: "inline-block", paddingBottom: 4 }}
      >
        {hasKeyString("approval") ? "Reviewer Comments" : "Reviewers Comments"}
      </Typography.Title>
      <Table
        columns={columns}
        dataSource={rows}
        loading={isLoading}
        pagination={false}
        rowKey={(record) => String(record.id ?? `${record.commentedOn}-${record.comment}`)}
        scroll={{ x: "max-content" }}
        onChange={(_pagination, _filters, sorter: any) => {
          if (!sorter?.field) return;
          setOrderBy(String(sorter.field));
          setOrder(sorter.order === "ascend" ? "asc" : "desc");
          setPage(0);
        }}
      />
      <Pagination
        style={{ marginTop: 16, textAlign: "right" }}
        current={page + 1}
        pageSize={PAGE_SIZE}
        total={totalRecords}
        showSizeChanger={false}
        onChange={(next) => setPage(next - 1)}
      />
    </>
  );
};
